/**
 * Screen 14a — the admin dashboard.
 *
 * Shows only the tiles this volunteer's roles actually open. The API refuses
 * the rest either way; hiding them is so a DOOR volunteer is not looking at a
 * Members tile that would 403 at them.
 *
 * The one thing this screen surfaces beyond navigation is the list of
 * committee questions still running on a default. Those are decisions nobody
 * has made, and the app is quietly assuming an answer until they do — worth a
 * banner rather than a buried settings row.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Note, Shell, type Area } from "../../components/Shell";
import { api, type Me } from "../../lib/api";

const TILES: { to: string; area: Area; key: string; capability: string }[] = [
  { to: "/admin/programs", area: "programs", key: "admin.programs", capability: "manage_programs" },
  { to: "/admin/members", area: "membership", key: "admin.members", capability: "manage_members" },
  { to: "/admin/settings", area: "about", key: "admin.settings", capability: "assign_roles" },
  { to: "/admin/scan", area: "calendar", key: "admin.scan", capability: "scan" },
];

export default function AdminHome() {
  const { t } = useTranslation();
  const [me, setMe] = useState<Me | null>(null);
  const [pending, setPending] = useState<string[]>([]);

  useEffect(() => {
    api.me().then(setMe).catch(() => undefined);
    // Only an admin who can reach settings sees the outstanding questions.
    api.admin
      .settings()
      .then((body) => setPending(body.unanswered.map((q) => q.key)))
      .catch(() => undefined);
  }, []);

  const allowed = TILES.filter((tile) => me?.capabilities[tile.capability]);

  return (
    <Shell area="about" title={t("admin.dashboard")}>
      {me && (
        /* The name, then the roles as their own marks. A single string joined
           with dots reads as one fact when it is six. */
        <div className="whoami">
          <span className="whoami__name">{me.name}</span>
          <span className="whoami__roles">
            {me.roles.map((role) => (
              <span key={role} className="badge badge--quiet">{role}</span>
            ))}
          </span>
        </div>
      )}

      {pending.length > 0 && (
        <div data-area="news" className="notice">
          <p>
            <strong>{t("admin.unanswered", { count: pending.length })}</strong>
          </p>
          <p className="fallback-note">{pending.join(", ")}</p>
          <Link className="btn" to="/admin/settings">
            {t("admin.settings")}
          </Link>
        </div>
      )}

      <div className="tiles">
        {allowed.map((tile) => (
          <Link key={tile.to} to={tile.to} data-area={tile.area} className="tile">
            {t(tile.key)}
          </Link>
        ))}
      </div>

      {me && allowed.length === 0 && <Note>{t("admin.no_access")}</Note>}

      <Link className="btn" to="/">
        {t("nav.home")}
      </Link>
    </Shell>
  );
}
